import React, { FunctionComponent } from 'react';
import styles from './AddToCalendarButton.scss';
import { addToCalendarHandler, CalendarType } from './helpers/addToCalendar';
import appleCalendarIcon from './images/apple-calendar-icon.svg';
import googleCalendarIcon from './images/google-calendar-icon.svg';
import outlookCalendarIcon from './images/outlook-calendar-icon.svg';
import outlookWebCalendarIcon from './images/outlook-web-calendar-icon.svg';
import yahooCalendarIcon from './images/yahoo-calendar-icon.svg';
import generateHash from '../../utils/generateHash';
import parseCurrency from '../../utils/parseCurrency';
import Button from '../Button/Button';

import queryString from 'query-string';
import { useTranslation } from 'react-i18next';

// OTG 2023 - Tom Hedges

type AddToCalendarButtonProps = {
  buttonText?: string;
  ownerName?: string;
  currencyCode: string;
  targetAmount?: number;
  charityName: string;
  eventName: string;
  calendarType: CalendarType;
  url: string;
  startTime: Date;
  endTime?: Date;
  classNames?: string;
};

export const AddToCalendarButton: FunctionComponent<AddToCalendarButtonProps> = ({
  ownerName,
  targetAmount,
  currencyCode,
  charityName,
  eventName,
  calendarType,
  startTime,
  endTime,
  buttonText,
  classNames,
  url,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const handleButtonClick = () => {
    const title = ownerName
      ? t('donationReminder.calendar.title.withOwner', {
          ownerName,
          charityName,
        })
      : t('donationReminder.calendar.title.withNoOwner', {
          eventName,
          charityName,
        });

    const targetAmountFormatted = targetAmount
      ? parseCurrency(currencyCode, targetAmount, {
          roundDown: true,
          wholeNumber: true,
        })
      : null;
    const shareHash = generateHash();
    const urlWithQueryString = `${url}?${queryString.stringify({
      utm_source: `${calendarType.toLowerCase()}-reminder`,
      utm_term: shareHash,
    })}`;
    const doubleLineSpacing =
      calendarType === CalendarType.Apple ||
      calendarType === CalendarType.Outlook
        ? '\\n\\n'
        : '\n\n';

    let descriptionLine1 = '';
    if (ownerName) {
      descriptionLine1 = targetAmountFormatted
        ? t('donationReminder.calendar.descriptionLine1.withOwnerWithTarget', {
            ownerName,
            eventName,
            targetAmountFormatted,
            charityName,
          })
        : t('donationReminder.calendar.descriptionLine1.withOwnerNoTarget', {
            ownerName,
            eventName,
            charityName,
          });
    } else {
      descriptionLine1 = t(
        'donationReminder.calendar.descriptionLine1.withNoOwner',
        {
          charityName,
          eventName,
        },
      );
    }

    const description = `${descriptionLine1}${doubleLineSpacing}${t(
      'donationReminder.calendar.descriptionLine2',
      {
        url: urlWithQueryString,
      },
    )}`;

    const event = {
      startTime,
      endTime: endTime || startTime,
      location: t('common.justgiving'),
      title,
      description,
    };

    addToCalendarHandler(event, calendarType);
  };

  const label = buttonText || calendarType;

  return (
    <Button
      type="button"
      onClick={handleButtonClick}
      className={`${styles.reminder} ${classNames || ''}`}
      outlined
      data-qa={`add-calendar-button-${calendarType.toLowerCase()}`}
    >
      <img src={getCalendarIcon(calendarType)} alt="" />
      {label}
    </Button>
  );
};

const getCalendarIcon = (calendarType: CalendarType): string => {
  switch (calendarType) {
    case CalendarType.Apple:
      return appleCalendarIcon;
    case CalendarType.Google:
      return googleCalendarIcon;
    case CalendarType.Outlook:
      return outlookCalendarIcon;
    case CalendarType.OutlookWeb:
      return outlookWebCalendarIcon;
    case CalendarType.Yahoo:
      return yahooCalendarIcon;
    default:
      return '';
  }
};
